import React from "react";

const HTMLStyleGuide = () => {
  return (
    <div className="html-style-guide">
      <h1>HTML Style Guide</h1>

      {/* 1. Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          A consistent HTML style guide helps maintain readable, maintainable, and scalable code. It ensures proper semantics, accessibility, and performance.
        </p>
      </section>

      {/* 2. File Structure and Organization */}
      <section id="file-structure">
        <h2>2. File Structure and Organization</h2>
        <ul>
          <li>Use lowercase for file names: <code>index.html</code>, <code>about.html</code></li>
          <li>Organize assets in folders: <code>css/</code>, <code>js/</code>, <code>images/</code></li>
          <li>Use semantic structure: <code>header</code>, <code>main</code>, <code>footer</code></li>
          <li>Include <code>&lt;!DOCTYPE html&gt;</code> at the top for HTML5</li>
        </ul>
      </section>

      {/* 3. Indentation and Formatting */}
      <section id="indentation">
        <h2>3. Indentation and Formatting</h2>
        <ul>
          <li>Use 2 or 4 spaces for indentation (avoid tabs).</li>
          <li>One element per line for readability.</li>
          <li>Close all elements properly and consistently.</li>
        </ul>
        <pre>
          {`<header>
  <h1>Site Title</h1>
  <nav>
    <ul>
      <li><a href="/">Home</a></li>
      <li><a href="/about.html">About</a></li>
    </ul>
  </nav>
</header>`}
        </pre>
      </section>

      {/* 4. Naming Conventions */}
      <section id="naming">
        <h2>4. Naming Conventions</h2>
        <ul>
          <li>Use descriptive class and ID names: <code>.main-nav</code>, <code>#contact-form</code></li>
          <li>Use kebab-case for class names: <code>.footer-links</code></li>
          <li>IDs should be unique per page.</li>
          <li>Use semantic names instead of styling hints: avoid <code>.red-text</code></li>
        </ul>
      </section>

      {/* 5. Semantic HTML */}
      <section id="semantic-html">
        <h2>5. Semantic HTML</h2>
        <ul>
          <li>Prefer semantic elements over generic <code>&lt;div&gt;</code> and <code>&lt;span&gt;</code>.</li>
          <li>Use <code>&lt;header&gt;</code>, <code>&lt;footer&gt;</code>, <code>&lt;section&gt;</code>, <code>&lt;article&gt;</code>, <code>&lt;aside&gt;</code> appropriately.</li>
          <li>Use text-level semantics: <code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>, <code>&lt;mark&gt;</code>.</li>
        </ul>
      </section>

      {/* 6. Comments */}
      <section id="comments">
        <h2>6. Comments</h2>
        <ul>
          <li>Use comments for sectioning and explanations:</li>
        </ul>
        <pre>
          {`<!-- Main Navigation -->
<nav>...</nav>

<!-- Featured Articles Section -->
<section>...</section>`}
        </pre>
      </section>

      {/* 7. Attributes */}
      <section id="attributes">
        <h2>7. Attributes</h2>
        <ul>
          <li>Use lowercase for attribute names: <code>class="main"</code></li>
          <li>Use quotes around attribute values: <code>href="index.html"</code></li>
          <li>Boolean attributes should be written without a value: <code>disabled</code>, <code>checked</code></li>
        </ul>
      </section>

      {/* 8. Accessibility */}
      <section id="accessibility">
        <h2>8. Accessibility</h2>
        <ul>
          <li>Always provide <code>alt</code> for images.</li>
          <li>Use <code>aria-label</code> or <code>role</code> when necessary.</li>
          <li>Ensure proper heading order: <code>h1 → h2 → h3</code>.</li>
          <li>Use descriptive link text instead of "click here".</li>
        </ul>
      </section>

      {/* 9. Links */}
      <section id="links">
        <h2>9. Links</h2>
        <ul>
          <li>Use relative paths for internal links: <code>href="/about.html"</code></li>
          <li>Use absolute URLs for external links: <code>href="https://example.com"</code></li>
          <li>Add <code>rel="noopener noreferrer"</code> for links with <code>target="_blank"</code></li>
        </ul>
      </section>

      {/* 10. Best Practices */}
      <section id="best-practices">
        <h2>10. Best Practices</h2>
        <ul>
          <li>Validate HTML using <a href="https://validator.w3.org/">W3C Validator</a>.</li>
          <li>Keep HTML clean and minimal; avoid unnecessary divs.</li>
          <li>Use CSS for styling; do not use HTML for presentation.</li>
          <li>Test pages across different browsers and devices.</li>
        </ul>
      </section>

      {/* 11. Summary */}
      <section id="summary">
        <h2>11. Summary</h2>
        <p>
          Following an HTML style guide ensures consistent, readable, semantic, and accessible web pages. It improves maintainability and overall code quality.
        </p>
      </section>
    </div>
  );
};

export default HTMLStyleGuide;
